/**
 * Customization panel: builds color fields, binds inputs to option changes, and reflects
 * the current options back into the controls (including which controls apply to the
 * selected graph type).
 */
import {
    BOOLEAN_FIELDS,
    ENUM_FIELDS,
    GRAPH_TYPES,
    GraphOptions,
    GraphType,
    METRIC_NAMES,
    NUMBER_FIELDS,
    SERIES_MODES,
    SeriesMode,
    RANGES,
    RangeName,
    FIELD_SCOPE,
    TITLE_MAX_LENGTH,
    normalizeColor,
    parseNumber,
} from '../src/graph/options';
import { METRICS } from '../src/graph/shared/aggregate';
import { toIsoDate } from '../src/graph/shared/dates';
import { GraphColors, ThemeName } from '../src/graph/themes';
import { byId } from './dom';

export type ControlChange =
    | { kind: 'type'; type: GraphType }
    | { kind: 'theme'; theme: ThemeName }
    | { kind: 'range'; range: RangeName }
    | { kind: 'window'; from: string; to: string }
    | { kind: 'color'; key: keyof GraphColors; value: string }
    | { kind: 'option'; apply: (options: GraphOptions) => void };

const COLOR_FIELDS: Array<[keyof GraphColors, string]> = [
    ['background', 'Background'],
    ['text', 'Title text'],
    ['border', 'Border'],
    ['empty', 'Empty cells'],
    ['color1', 'Low'],
    ['color2', 'Medium'],
    ['color3', 'High'],
    ['color4', 'Highest'],
    ['line', 'Line'],
    ['point', 'Points'],
    ['pointBorder', 'Point border'],
    ['area', 'Area fill'],
    ['grid', 'Grid'],
    ['bar', 'Bars'],
];

type EnumKey = keyof typeof ENUM_FIELDS;

const TYPE_COLOR_FIELDS: Array<[keyof GraphColors, string]> = [
    ['label', 'Label color'],
    ['count', 'Count color'],
];

type NumberKey = keyof typeof NUMBER_FIELDS;

const pickerValue = (color: string) =>
    color === 'transparent' ? '#000000' : `#${color.slice(0, 6)}`;

const colorField = ([key, label]: [keyof GraphColors, string]): HTMLElement => {
    const wrapper = document.createElement('div');
    wrapper.className = 'color-field';
    const id = `color-${key}`;
    // Colors that only apply to one graph type are hidden for the other.
    const scope = FIELD_SCOPE[key];
    if (scope) wrapper.dataset.scope = scope.join(' ');
    wrapper.innerHTML =
        `<label class="field-label" for="${id}">${label}</label>` +
        `<div class="color-input">` +
        `<input type="color" data-color="${key}" aria-label="${label} color picker" tabindex="-1" />` +
        `<input type="text" id="${id}" data-color-hex="${key}" maxlength="11" spellcheck="false" autocomplete="off" />` +
        `</div>`;
    return wrapper;
};

export const createControls = (onChange: (change: ControlChange) => void) => {
    const form = byId<HTMLFormElement>('controls');
    byId('color-grid').append(...COLOR_FIELDS.map(colorField));
    byId('type-color-grid').append(...TYPE_COLOR_FIELDS.map(colorField));
    for (const select of form.querySelectorAll<HTMLSelectElement>('.metric-select')) {
        select.innerHTML = METRIC_NAMES.map(
            (name) =>
                `<option value="${name}">${name === 'auto' ? 'Automatic' : METRICS[name].label}</option>`,
        ).join('');
    }

    const radios = (name: string) => [
        ...form.querySelectorAll<HTMLInputElement>(`input[name="${name}"]`),
    ];
    const typeInputs = radios('type');
    const themeInputs = radios('theme');
    const seriesInputs = radios('series');
    const optionInputs = [
        ...form.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-option]'),
    ];
    const windowInputs = [...form.querySelectorAll<HTMLInputElement>('[data-window]')];
    const pickers = [...form.querySelectorAll<HTMLInputElement>('[data-color]')];
    const hexInputs = [...form.querySelectorAll<HTMLInputElement>('[data-color-hex]')];
    const outputs = [...form.querySelectorAll<HTMLOutputElement>('[data-output]')];
    const scoped = [...form.querySelectorAll<HTMLElement>('[data-scope]')];
    const customRange = byId('custom-range');
    const rangeHint = byId('range-hint');

    form.addEventListener('submit', (event) => event.preventDefault());

    for (const input of typeInputs) {
        input.addEventListener('change', () => {
            const type = input.value as GraphType;
            if (input.checked && GRAPH_TYPES.includes(type)) onChange({ kind: 'type', type });
        });
    }

    for (const input of themeInputs) {
        input.addEventListener('change', () => {
            if (input.checked) onChange({ kind: 'theme', theme: input.value as ThemeName });
        });
    }

    for (const input of seriesInputs) {
        input.addEventListener('change', () => {
            const series = input.value as SeriesMode;
            if (input.checked && SERIES_MODES.includes(series))
                onChange({ kind: 'option', apply: (o) => (o.series = series) });
        });
    }

    for (const picker of pickers) {
        picker.addEventListener('input', () => {
            const key = picker.dataset.color as keyof GraphColors;
            const value = normalizeColor(picker.value);
            if (value) onChange({ kind: 'color', key, value });
        });
    }

    for (const hex of hexInputs) {
        hex.addEventListener('input', () => {
            const key = hex.dataset.colorHex as keyof GraphColors;
            const value = normalizeColor(hex.value);
            hex.setAttribute('aria-invalid', String(!value));
            if (value) onChange({ kind: 'color', key, value });
        });
    }

    const emitWindow = () => {
        const [from, to] = ['from', 'to'].map(
            (name) => windowInputs.find((input) => input.dataset.window === name)?.value ?? '',
        );
        if (from && to) onChange({ kind: 'window', from, to });
    };
    for (const input of windowInputs) input.addEventListener('change', emitWindow);

    for (const input of optionInputs) {
        const key = input.dataset.option as keyof GraphOptions;
        const eventName =
            input instanceof HTMLSelectElement || input.type === 'checkbox' ? 'change' : 'input';
        input.addEventListener(eventName, () => {
            if (input instanceof HTMLInputElement && input.type === 'checkbox') {
                const checked = input.checked;
                onChange({
                    kind: 'option',
                    apply: (o) => (o[key as (typeof BOOLEAN_FIELDS)[number]] = checked),
                });
            } else if (key in NUMBER_FIELDS) {
                const field = NUMBER_FIELDS[key as NumberKey];
                const value = input.value === '' ? 0 : parseNumber(input.value, field);
                if (value !== undefined)
                    onChange({ kind: 'option', apply: (o) => (o[key as NumberKey] = value) });
            } else if (key === 'range') {
                const range = input.value as RangeName;
                if (RANGES.includes(range)) onChange({ kind: 'range', range });
            } else if (key in ENUM_FIELDS) {
                const value = input.value;
                if (ENUM_FIELDS[key as EnumKey].includes(value))
                    onChange({
                        kind: 'option',
                        apply: (o) => ((o[key as EnumKey] as string) = value),
                    });
            } else if (key === 'title') {
                const title = input.value.slice(0, TITLE_MAX_LENGTH);
                onChange({ kind: 'option', apply: (o) => (o.title = title.trim()) });
            }
        });
    }

    /** Writes options into the controls. Skips the element the user is typing in. */
    const sync = (options: GraphOptions) => {
        const active = document.activeElement;
        for (const input of typeInputs) input.checked = input.value === options.type;
        for (const input of themeInputs) input.checked = input.value === options.theme;
        for (const input of seriesInputs) input.checked = input.value === options.series;
        for (const element of scoped)
            element.hidden = !(element.dataset.scope ?? '').split(' ').includes(options.type);

        const isCustom = options.range === 'custom';
        customRange.hidden = !isCustom;
        rangeHint.hidden = !isCustom;
        const today = toIsoDate(Date.now());
        for (const input of windowInputs) {
            input.max = today;
            if (input !== active)
                input.value = input.dataset.window === 'from' ? options.from : options.to;
        }

        for (const picker of pickers) {
            const key = picker.dataset.color as keyof GraphColors;
            if (picker !== active) picker.value = pickerValue(options.colors[key]);
        }
        for (const hex of hexInputs) {
            const key = hex.dataset.colorHex as keyof GraphColors;
            if (hex !== active) {
                hex.value = options.colors[key];
                hex.removeAttribute('aria-invalid');
            }
        }

        for (const input of optionInputs) {
            if (input === active && input.type !== 'range' && input.type !== 'checkbox') continue;
            const key = input.dataset.option as keyof GraphOptions;
            const value = options[key];
            if (input instanceof HTMLInputElement && input.type === 'checkbox')
                input.checked = Boolean(value);
            else if (input instanceof HTMLInputElement && input.type === 'number')
                input.value = value ? String(value) : '';
            else input.value = String(value ?? '');
        }

        for (const output of outputs) {
            const key = output.dataset.output as NumberKey;
            output.value = `${options[key]}${output.dataset.unit ?? 'px'}`;
        }
    };

    // Re-normalize free-text fields when leaving them (e.g. width 5 -> 200, bad hex -> last good).
    form.addEventListener('focusout', () => setTimeout(() => lastOptions && sync(lastOptions)));
    let lastOptions: GraphOptions | undefined;

    return {
        sync(options: GraphOptions) {
            lastOptions = options;
            sync(options);
        },
    };
};
